import { useState, useEffect } from 'react';
import type { AdminWorldDetail, AdminWorldState } from '../../../lib/api';
import { adminApi } from '../../../lib/api';

interface StyleProfile {
  voice?: string;
  pov?: 'first_person' | 'second_person' | 'third_person_limited' | 'third_person_omniscient';
  tone?: string[];
  formality?: number;
  lexicon?: string[];
  forbidden?: string[];
  formatting?: {
    max_paragraphs?: number;
    include_salutation?: boolean;
    include_signoff?: boolean;
    include_date_stamp?: boolean;
  };
}

interface WorldConfigurationProps {
  world: AdminWorldDetail;
  state: AdminWorldState | null;
  onUpdate: () => Promise<void>;
}

export function WorldConfiguration({ world, state: _state, onUpdate }: WorldConfigurationProps) {
  const [activeTab, setActiveTab] = useState<'style' | 'llm' | 'delivery'>('style');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl text-parchment">Configuration</h1>
        <p className="text-mist text-sm mt-1">Style, LLM, and delivery settings</p>
      </div>

      {/* Tabs */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        <div className="flex border-b border-stone/30">
          {[
            { id: 'style', label: 'Style' },
            { id: 'llm', label: 'LLM Settings' },
            { id: 'delivery', label: 'Delivery' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-4 py-3 text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-rune/20 text-rune border-b-2 border-rune'
                  : 'text-mist hover:text-parchment hover:bg-stone/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'style' && (
            <StyleEditor worldId={world.id} onUpdate={onUpdate} />
          )}
          {activeTab === 'llm' && (
            <LLMEditor worldId={world.id} onUpdate={onUpdate} />
          )}
          {activeTab === 'delivery' && (
            <DeliverySettings world={world} onUpdate={onUpdate} />
          )}
        </div>
      </div>
    </div>
  );
}

function StyleEditor({ worldId, onUpdate }: { worldId: string; onUpdate: () => Promise<void> }) {
  const [styleProfile, setStyleProfile] = useState<StyleProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const fetchStyle = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const state = await adminApi.getWorldState(worldId);
        const style = state.style as { style_profile?: StyleProfile } | null;
        setStyleProfile(style?.style_profile ?? {});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load style');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStyle();
  }, [worldId]);

  const handleSave = async () => {
    if (!styleProfile || !reason.trim()) {
      setError('Please provide a reason for the change');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await adminApi.updateWorldStyle(worldId, {
        ...styleProfile,
        reason,
      });
      setSuccess('Style settings updated successfully');
      setReason('');
      await onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save style');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-mist animate-pulse py-4">Loading style settings...</div>;
  }

  if (!styleProfile) {
    return <div className="text-ember text-sm">{error ?? 'No style data available'}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Voice */}
      <div>
        <label className="block text-mist text-sm mb-1">Voice (narrator persona)</label>
        <textarea
          value={styleProfile.voice ?? ''}
          onChange={(e) => setStyleProfile({ ...styleProfile, voice: e.target.value })}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm resize-none"
          rows={2}
          placeholder="A mysterious narrator..."
        />
      </div>

      {/* POV */}
      <div>
        <label className="block text-mist text-sm mb-1">Point of View</label>
        <select
          value={styleProfile.pov ?? 'first_person'}
          onChange={(e) => setStyleProfile({ ...styleProfile, pov: e.target.value as StyleProfile['pov'] })}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
        >
          <option value="first_person">First Person</option>
          <option value="second_person">Second Person</option>
          <option value="third_person_limited">Third Person Limited</option>
          <option value="third_person_omniscient">Third Person Omniscient</option>
        </select>
      </div>

      {/* Tone */}
      <div>
        <label className="block text-mist text-sm mb-1">Tone (comma-separated)</label>
        <input
          type="text"
          value={(styleProfile.tone ?? []).join(', ')}
          onChange={(e) =>
            setStyleProfile({
              ...styleProfile,
              tone: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
          placeholder="mysterious, whimsical, epistolary"
        />
      </div>

      {/* Formality */}
      <div>
        <label className="block text-mist text-sm mb-1">
          Formality: {((styleProfile.formality ?? 0.5) * 100).toFixed(0)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={styleProfile.formality ?? 0.5}
          onChange={(e) => setStyleProfile({ ...styleProfile, formality: parseFloat(e.target.value) })}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-mist/50">
          <span>Casual</span>
          <span>Formal</span>
        </div>
      </div>

      {/* Max Paragraphs */}
      <div>
        <label className="block text-mist text-sm mb-1">
          Max Paragraphs: {styleProfile.formatting?.max_paragraphs ?? 8}
        </label>
        <input
          type="range"
          min="3"
          max="20"
          step="1"
          value={styleProfile.formatting?.max_paragraphs ?? 8}
          onChange={(e) =>
            setStyleProfile({
              ...styleProfile,
              formatting: { ...styleProfile.formatting, max_paragraphs: parseInt(e.target.value) },
            })
          }
          className="w-full"
        />
        <div className="flex justify-between text-xs text-mist/50">
          <span>Short (3)</span>
          <span>Long (20)</span>
        </div>
      </div>

      {/* Formatting Toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="flex items-center gap-2 text-sm text-parchment">
          <input
            type="checkbox"
            checked={styleProfile.formatting?.include_salutation ?? true}
            onChange={(e) =>
              setStyleProfile({
                ...styleProfile,
                formatting: { ...styleProfile.formatting, include_salutation: e.target.checked },
              })
            }
            className="rounded"
          />
          Salutation
        </label>
        <label className="flex items-center gap-2 text-sm text-parchment">
          <input
            type="checkbox"
            checked={styleProfile.formatting?.include_signoff ?? true}
            onChange={(e) =>
              setStyleProfile({
                ...styleProfile,
                formatting: { ...styleProfile.formatting, include_signoff: e.target.checked },
              })
            }
            className="rounded"
          />
          Sign-off
        </label>
        <label className="flex items-center gap-2 text-sm text-parchment">
          <input
            type="checkbox"
            checked={styleProfile.formatting?.include_date_stamp ?? false}
            onChange={(e) =>
              setStyleProfile({
                ...styleProfile,
                formatting: { ...styleProfile.formatting, include_date_stamp: e.target.checked },
              })
            }
            className="rounded"
          />
          Date Stamp
        </label>
      </div>

      {/* Forbidden Words */}
      <div>
        <label className="block text-mist text-sm mb-1">Forbidden Words (comma-separated)</label>
        <input
          type="text"
          value={(styleProfile.forbidden ?? []).join(', ')}
          onChange={(e) =>
            setStyleProfile({
              ...styleProfile,
              forbidden: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
          placeholder="AI, model, prompt, token"
        />
      </div>

      {/* Reason */}
      <div className="pt-4 border-t border-stone/20">
        <label className="block text-mist text-sm mb-1">Reason for Change (required)</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
          placeholder="Adjusted letter length per user request"
        />
      </div>

      {/* Status Messages */}
      {error && (
        <div className="text-ember text-sm bg-ember/10 rounded px-3 py-2">{error}</div>
      )}
      {success && (
        <div className="text-success text-sm bg-success/10 rounded px-3 py-2">{success}</div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isSaving || !reason.trim()}
        className="w-full py-2 px-4 bg-rune text-night rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rune/90 transition-colors"
      >
        {isSaving ? 'Saving...' : 'Save Style Changes'}
      </button>
    </div>
  );
}

function LLMEditor({ worldId, onUpdate }: { worldId: string; onUpdate: () => Promise<void> }) {
  const [settings, setSettings] = useState<{
    provider: string | null;
    modelPackage: string | null;
    generateModel: string | null;
    reviewModel: string | null;
    writingPolishModel: string | null;
    worldBuilderModel: string | null;
    utilityModel: string | null;
    embeddingModel: string | null;
    embeddingDimension: number;
  } | null>(null);
  const [resolved, setResolved] = useState<{
    provider: string;
    generateModel: string;
    reviewModel: string;
    writingPolishModel: string | null;
    worldBuilderModel: string;
    utilityModel: string;
    embeddingModel: string;
    embeddingDimension: number;
  } | null>(null);
  const [defaults, setDefaults] = useState<{
    provider: string;
    openai: Record<string, string | null>;
    fireworks: Record<string, string | null>;
  } | null>(null);
  const [packages, setPackages] = useState<Array<{ key: string; name: string; provider: string; tier: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const fetchLLMSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getWorldLLMSettings(worldId);
        setSettings(data.settings);
        setResolved(data.resolved);
        setDefaults(data.systemDefaults);
        setPackages(data.availablePackages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load LLM settings');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLLMSettings();
  }, [worldId]);

  const handleSave = async () => {
    if (!settings || !reason.trim()) {
      setError('Please provide a reason for the change');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminApi.updateWorldLLMSettings(worldId, {
        modelPackage: settings.modelPackage,
        provider: settings.provider,
        generateModel: settings.generateModel,
        reviewModel: settings.reviewModel,
        writingPolishModel: settings.writingPolishModel,
        worldBuilderModel: settings.worldBuilderModel,
        utilityModel: settings.utilityModel,
        embeddingModel: settings.embeddingModel,
        embeddingDimension: settings.embeddingDimension,
        reason,
      });
      setSettings(result.settings);
      const data = await adminApi.getWorldLLMSettings(worldId);
      setResolved(data.resolved);
      setSuccess('LLM settings updated successfully');
      setReason('');
      await onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save LLM settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-mist animate-pulse py-4">Loading LLM settings...</div>;
  }

  if (!settings || !defaults) {
    return <div className="text-ember text-sm">{error ?? 'Failed to load LLM settings'}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Model Package Selection */}
      <div>
        <label className="block text-mist text-sm mb-1">Model Package</label>
        <select
          value={settings.modelPackage ?? ''}
          onChange={(e) => setSettings({ ...settings, modelPackage: e.target.value || null })}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
        >
          <option value="">No Package (Custom / System Default)</option>
          {packages.map((pkg) => (
            <option key={pkg.key} value={pkg.key}>
              {pkg.name} ({pkg.tier})
            </option>
          ))}
        </select>
        <div className="text-xs text-mist/60 mt-1">
          Select a preset package or leave empty for custom configuration
        </div>
      </div>

      {/* Provider Selection */}
      <div>
        <label className="block text-mist text-sm mb-1">Provider Override</label>
        <select
          value={settings.provider ?? ''}
          onChange={(e) => setSettings({ ...settings, provider: e.target.value || null })}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
        >
          <option value="">Use Package/System Default ({defaults.provider})</option>
          <option value="openai">OpenAI</option>
          <option value="fireworks">Fireworks</option>
        </select>
      </div>

      {/* Resolved Configuration */}
      {resolved && (
        <div className="bg-rune/10 border border-rune/30 rounded-lg p-3">
          <div className="font-medium text-parchment text-sm mb-2">Resolved Configuration</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-mist">Provider:</span>{' '}
              <span className="text-parchment">{resolved.provider}</span>
            </div>
            <div>
              <span className="text-mist">Generate:</span>{' '}
              <span className="text-parchment font-mono">{resolved.generateModel}</span>
            </div>
            <div>
              <span className="text-mist">Review:</span>{' '}
              <span className="text-parchment font-mono">{resolved.reviewModel}</span>
            </div>
            <div>
              <span className="text-mist">Polish:</span>{' '}
              <span className="text-parchment font-mono">{resolved.writingPolishModel ?? 'disabled'}</span>
            </div>
            <div>
              <span className="text-mist">World Builder:</span>{' '}
              <span className="text-parchment font-mono">{resolved.worldBuilderModel}</span>
            </div>
            <div>
              <span className="text-mist">Utility:</span>{' '}
              <span className="text-parchment font-mono">{resolved.utilityModel}</span>
            </div>
            <div>
              <span className="text-mist">Embedding:</span>{' '}
              <span className="text-parchment font-mono">{resolved.embeddingModel}</span>
            </div>
            <div>
              <span className="text-mist">Dimension:</span>{' '}
              <span className="text-parchment">{resolved.embeddingDimension}</span>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Overrides */}
      <details className="cursor-pointer">
        <summary className="text-sm text-mist hover:text-parchment">Advanced Model Overrides</summary>
        <div className="mt-4 space-y-4 pl-2 border-l-2 border-stone/20">
          <div>
            <label className="block text-mist text-sm mb-1">Generate Model Override</label>
            <input
              type="text"
              value={settings.generateModel ?? ''}
              onChange={(e) => setSettings({ ...settings, generateModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to use package/system default"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">Review Model Override</label>
            <input
              type="text"
              value={settings.reviewModel ?? ''}
              onChange={(e) => setSettings({ ...settings, reviewModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to use package/system default"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">Writing Polish Model Override</label>
            <input
              type="text"
              value={settings.writingPolishModel ?? ''}
              onChange={(e) => setSettings({ ...settings, writingPolishModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to skip polishing"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">World Builder Model Override</label>
            <input
              type="text"
              value={settings.worldBuilderModel ?? ''}
              onChange={(e) => setSettings({ ...settings, worldBuilderModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to use package/system default"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">Utility Model Override</label>
            <input
              type="text"
              value={settings.utilityModel ?? ''}
              onChange={(e) => setSettings({ ...settings, utilityModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to use package/system default"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">Embedding Model Override</label>
            <input
              type="text"
              value={settings.embeddingModel ?? ''}
              onChange={(e) => setSettings({ ...settings, embeddingModel: e.target.value || null })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Leave empty to use package/system default"
            />
          </div>
          <div>
            <label className="block text-mist text-sm mb-1">Embedding Dimension</label>
            <input
              type="number"
              value={settings.embeddingDimension}
              onChange={(e) => setSettings({ ...settings, embeddingDimension: parseInt(e.target.value) || 1536 })}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              min={128}
              max={4096}
            />
          </div>
          <div className="bg-ember/10 border border-ember/30 rounded-lg p-3 text-xs text-ember">
            <strong>Warning:</strong> Changing the embedding model or dimension after content has been indexed will make existing embeddings incompatible.
          </div>
        </div>
      </details>

      {/* Reason */}
      <div className="pt-4 border-t border-stone/20">
        <label className="block text-mist text-sm mb-1">Reason for Change (required)</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
          placeholder="Switching to Fireworks Standard for cost optimization"
        />
      </div>

      {/* Status Messages */}
      {error && (
        <div className="text-ember text-sm bg-ember/10 rounded px-3 py-2">{error}</div>
      )}
      {success && (
        <div className="text-success text-sm bg-success/10 rounded px-3 py-2">{success}</div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isSaving || !reason.trim()}
        className="w-full py-2 px-4 bg-rune text-night rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rune/90 transition-colors"
      >
        {isSaving ? 'Saving...' : 'Save LLM Settings'}
      </button>
    </div>
  );
}

function DeliverySettings({ world, onUpdate }: { world: AdminWorldDetail; onUpdate: () => Promise<void> }) {
  const [autoReviewEnabled, setAutoReviewEnabled] = useState(world.autoReviewEnabled);
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isDirty = autoReviewEnabled !== world.autoReviewEnabled;

  const handleSave = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for the change');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await adminApi.updateWorldAutoReview(world.id, {
        enabled: autoReviewEnabled,
        reason: reason.trim(),
      });
      setSuccess('Auto review setting updated successfully');
      setReason('');
      await onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Read-only settings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-void/50 rounded-lg p-3">
          <div className="text-mist text-xs mb-1">Timezone</div>
          <div className="text-parchment">{world.timezone}</div>
        </div>
        <div className="bg-void/50 rounded-lg p-3">
          <div className="text-mist text-xs mb-1">Delivery Time</div>
          <div className="text-parchment">{world.deliveryTime}</div>
        </div>
        <div className="bg-void/50 rounded-lg p-3">
          <div className="text-mist text-xs mb-1">Address Mode</div>
          <div className="text-parchment">{world.addressMode}</div>
        </div>
        <div className="bg-void/50 rounded-lg p-3">
          <div className="text-mist text-xs mb-1">Inbound Address</div>
          <div className="text-parchment text-sm font-mono truncate">{world.inboundAddress}</div>
        </div>
      </div>

      {/* Auto Review Toggle */}
      <div className="pt-4 border-t border-stone/20">
        <label className="block text-sm text-mist mb-2">AI Auto Review</label>
        <div className="text-xs text-mist/70 mb-2">
          Automatically queue an AI review after each outbound letter.
        </div>
        <label className="flex items-center gap-2 text-sm text-parchment">
          <input
            type="checkbox"
            checked={autoReviewEnabled}
            onChange={(e) => setAutoReviewEnabled(e.target.checked)}
            className="rounded border-stone/30 bg-void"
          />
          {autoReviewEnabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      {isDirty && (
        <>
          <div>
            <label className="block text-mist text-sm mb-1">Reason for Change (required)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-stone/20 border border-stone/30 rounded px-3 py-2 text-parchment text-sm"
              placeholder="Enabling auto review for QA"
            />
          </div>

          {error && (
            <div className="text-ember text-sm bg-ember/10 rounded px-3 py-2">{error}</div>
          )}
          {success && (
            <div className="text-success text-sm bg-success/10 rounded px-3 py-2">{success}</div>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || !reason.trim()}
            className="w-full py-2 px-4 bg-rune text-night rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-rune/90 transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      )}
    </div>
  );
}
